import { z } from "zod";
import i18n from "@/i18n";

const t = (key: string) => i18n.t(key, { ns: "validation" });

/**
 * Wizard-only pet schema:
 * - `owner_id` is NOT required during wizard validation (owner may not exist yet)
 * - Other validations mirror `petSchema`
 *
 * IMPORTANT: Do not replace the shared `petSchema` in `src/lib/validations/pet.ts`
 * because other screens may rely on its stricter requirements.
 */
export function createPetWizardSchema(deceasedStatusId?: string) {
  return petWizardSchemaBase.superRefine((data, ctx) => {
    if (!data.death_date) {
      if (deceasedStatusId && data.status_id === deceasedStatusId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["death_date"],
          message: t("deathDateRequired"),
        });
      }
      return;
    }
    const date = new Date(data.death_date);
    if (Number.isNaN(date.getTime()) || date > new Date()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["death_date"],
        message: t("deathDateNotFuture"),
      });
    }
  });
}

const petWizardSchemaBase = z.object({
  name: z
    .string()
    .trim()
    .min(1, t("petNameRequired"))
    .max(100, t("petNameTooLong")),
  record_number: z
    .string()
    .trim()
    .max(50, t("recordNumberTooLong"))
    .optional()
    .or(z.literal("")),
  owner_id: z.string().uuid().optional().or(z.literal("")), // optional in wizard
  gender_id: z.string().uuid().optional().or(z.literal("")),
  sterilization_id: z.string().uuid().optional().or(z.literal("")),
  birth_date: z
    .string()
    .optional()
    .or(z.literal(""))
      .refine(
      (val) => {
        if (!val) return true;
        const date = new Date(val);
        return date <= new Date();
      },
      { message: t("birthDateNotFuture") },
    ),
  species_id: z.string().uuid(t("speciesRequired")),
  breed_id: z.string().uuid().optional().or(z.literal("")),
  size_category_id: z.string().uuid().optional().or(z.literal("")),
  coat_type_id: z.string().uuid().optional().or(z.literal("")),
  color_id: z.string().uuid().optional().or(z.literal("")),
  status_id: z.string().uuid(t("statusRequired")),
  death_date: z.string().optional().or(z.literal("")),
  death_cause: z.string().trim().optional().or(z.literal("")),
  microchip_number: z.string().trim().optional().or(z.literal("")),
  health_plan: z.string().trim().optional().or(z.literal("")),
  pedigree_number: z.string().trim().optional().or(z.literal("")),
  notes: z.string().trim().optional().or(z.literal("")),
  current_weight: z.number().positive(t("weightPositive")).max(9999, t("weightMax")).optional(),
  allergies: z.array(z.string().uuid()).optional(),
  temperaments: z.array(z.string().uuid()).optional(),
  photo: z.any().optional(),
  attachments: z.array(z.any()).optional(),
});

export const petWizardSchema = createPetWizardSchema();

export type PetWizardInput = z.infer<typeof petWizardSchemaBase>;
